import { ACNElement, AllElementTypes, ButtonElement, CalculatedElement, ChartElement, CheckboxListElement, ContainerElement, ContainerState, DropdownElement, DynamicDropdownElement, EntityLookupElement, EventContext, FieldMessages, GroupElement, HeadingElement, IconChoiceElement, InfoElement, ListElement, PresenterState, RadioElement, RefTableElement, ReportElement, ScriptElement, SuccessionPlanElement, TextElement } from "./types";
import { Text } from "../core/form/Text";
import { Container } from "./Container";
import { DataState, ValidationContext } from "../core/hooks/useData";
import { ageRule, charsLimitRule, dataTypeRule, rangeRule, requiredRule, validACNRule, validDateRule } from "../core/form/validators";
import { Field } from "./Field";
import { Checkbox } from "../core/form/Checkbox";
import { Radio } from "../core/form/Radio";
import { Info } from "./Info";
import { Dropdown } from "../core/form/Dropdown";
import { List } from "../core/form/List";
import { Heading } from "./Heading";
import { newID } from "../core/utils/unique";
import { Calc } from "./Calc";
import { Date } from "../core/form/Date";
import { LegalElement } from "./LegalElement";
import { AusAddressSearch } from "./AusAddressSearch";
import { ResourcePicker } from "./ResourcePicker";
import { ReadOnlyValue } from "./ReadOnlyValue";
import { broadcast } from "../core/hooks/useChannel";
import { customRule, occurrenceRangeRule } from "./runScript";
import { ACN } from "../core/form/ACN";
import { EntityLookup } from "./EntityLookup";
import { toBool } from "../core/utils/conversion";
import { RefTable } from "../types";
import { Report } from "./Report";
import { SuccessionPlan } from "./SuccessionPlan";
import { Group } from "./Group";
import { CheckboxList } from "../core/form/CheckboxList";
import { IconChoice } from "./IconChoice";
import { DynamicDropdown } from "./DynamicDropdown";
import { Choices } from "./Choices";
import { Chart } from "./Chart";
import { formatDate } from "rant-utils";
import { GatewayForm } from "../core/form/GatewayForm";
import { JSEditor } from "../core/ui/JSEditor";


export function actionValidator(context: ValidationContext<any>, def: AllElementTypes, presenter: PresenterState, container: ContainerState) {

	// let haveCheckedExists = false;

	// function checkExists() {
	// 	if (haveCheckedExists) return true;
	// 	haveCheckedExists = true;
	// 	return (document.getElementById(def.uniqueID!) !== null);
	// };

	// shortcuts
	if ((def as any).required) {
		// if (!checkExists()) return;
		requiredRule(context, def.id);
	}

	if (!def.validations) return;

	// if (!checkExists()) return;

	for(let v of def.validations) {
		if (v.type === "required") {
			requiredRule(context, def.id);
		
		} else if (v.type === "char-limit") {
			charsLimitRule(context, def.id, v.options);

		} else if (v.type === "range") {
			rangeRule(context, def.id, v.options);

		} else if (v.type === "data-type") {
			dataTypeRule(context, def.id, v.options);

		} else if (v.type === "age") {
			ageRule(context, def.id, v.options);

		} else if (v.type === "occurrence-range") {
			occurrenceRangeRule(context, def.id, v.options, presenter);

		} else if (v.type === "custom") {
			customRule(context, def.id, v.options, presenter, container, def);

		}
	}

}

function prepRefTableElement(presenter: PresenterState, def: RefTableElement, container: ContainerState) {
	const initialData = presenter.getRefData(def.ref);
	let refData: RefTable;

	// if we have a filter col specified then need to first filter the list
	if (def.filterCol) {
		const filtered = {
			...initialData,
			rows: [] as any,
		};
		for(let r of initialData.rows) {
			if (toBool(r[def.filterCol])) {
				filtered.rows.push(r);
			}
		}
		refData = filtered;

	} else if (def.filterScript) {
		const filtered = {
			...initialData,
			rows: [] as any,
		};
		const ctx: EventContext = {
			container,
			dataWindows: [{
				name: "data",
				obj: container.data,
			}],
			def,
			presenter,
			type: "change",
			options: container.options,
			debugContext: [
				"RefTable.filter"
			]
		}
		for(let row of initialData.rows) {
			if (toBool(presenter.scriptEngine.run(def.filterScript, ctx, [ { row } ]))) {
				filtered.rows.push(row);
			}
		}
		refData = filtered;

	} else {
		refData = initialData;
	}
 
	const colMap = {} as any;
	for(let c of refData.columns) {
		colMap[c.id] = c;
	}

	function getValue(option: any, specificColumn?: string) {
		const id = specificColumn ? specificColumn : def.valueCol;
		let val = option[id];

		const c = colMap[id];
		if (c && c.datatype) {
			if (c.datatype === "boolean") val = toBool(val);
		}

		return val;
	}
	function getDisplay(option: any, selected: boolean) {
		const val = option[def.textCol];
		return val;			
	}
	const getSubtitle = def.subtitleCol ?
		(option: any) => {
			const subtitle = (def.subtitleCol ? option[def.subtitleCol] : undefined);
			return subtitle;
		}
		: undefined;

	function getDisplayFor(value: any) {
		for(let r of refData.rows) {
			if (getValue(r) === value) return getDisplay(r, false);
		}
		return "";
	}

	return {
		refData,
		getValue, 
		getDisplay,
		getDisplayFor,
		getSubtitle,
	}
}

export function createElement(def: AllElementTypes, presenter: PresenterState, data: DataState<any>, container: ContainerState) {
	let component;

	const designerKeyAppend = presenter.dataStates ? newID() : "";

	const defaultProps = {
		prop: def.id,
		errorsDisabled: true,
		
		placeholder: (def.titleType === "placeholder") ? def.title : undefined,
	};

	let noLabel = (def.titleType === "placeholder");
	let noErrors = false;

	// make sure the def has a unique id
	if (!def.uniqueID) def.uniqueID = newID();

	function checkVisible() {
		const f = container.fields[def.id];
		if (f) {
			const msg = {
				type: FieldMessages.GET_VISIBILITY,
				visible: true, // assume its visible unless we find its not
			};
			broadcast(f.channelID, msg);
			return msg.visible;
		}
		return false;
	}
	
	if (def.type === "text") {
		const text = def as TextElement;
		if (presenter.readOnly) {
			component = <ReadOnlyValue>{data.getValue(def.id)}</ReadOnlyValue>;
		} else {
			component = 
				<Text {...defaultProps}
					typeIndicator={text.typeIndicator as any}
					isMultiline={text.multiline}
					isWide={text.wide}
					size={text.size}
				/>;

			if (text.quickChoices) {
				const choices = text.quickChoices.split(",");
				component = (
					<div className="flex vert align-start">
						<Choices choices={choices} propToSet={text.id} />
						{component}
					</div>
				);

			}
		}

	} else if (def.type === "acn") {
		const acn = def as ACNElement;
		if (presenter.readOnly) {
			component = <ReadOnlyValue>{data.getValue(def.id)}</ReadOnlyValue>;
		} else {
			component = 
				<ACN {...defaultProps}
				/>;
		}
		
		data.validators!.push(
			{
				validate: (context: ValidationContext<any>) => {
					if (!checkVisible()) return;

					validACNRule(context, def.id, def.required!);
				}
			}
		);


	} else if (def.type === "aus-address-search") {
		component = 
			<AusAddressSearch
				{...defaultProps}
			/>;

	} else if (def.type === "heading") {
		component = <Heading {...defaultProps} def={def as HeadingElement} />;
		noLabel = true;
		//noErrors = true;

	} else if (def.type === "checkbox") {
		if (presenter.readOnly) {
			const b = data.getValue(def.id);
			component = <ReadOnlyValue>{b ? "Yes" : "No"}</ReadOnlyValue>;
		} else {
			component = <Checkbox {...defaultProps} />;
		}

	} else if (def.type === "info") {
		component = <Info {...defaultProps} def={def as InfoElement} />;
		
	} else if (def.type === "radio") {
		const radio = def as RadioElement;
		const refProps = prepRefTableElement(presenter, radio, container);

		if (presenter.readOnly) {
			const val = data.getValue(def.id);
			const text = refProps.getDisplayFor(val);
			component = <ReadOnlyValue>{text}</ReadOnlyValue>;
		} else {	
			component = 
				<Radio 
					{...defaultProps} 
					verticalLayout={radio.layout === "vertical"}
					options={refProps.refData ? refProps.refData.rows : []}
					getValue={refProps.getValue}
					getDisplay={refProps.getDisplay}
					getSubtitle={refProps.getSubtitle}
				/>;
		}

	} else if (def.type === "list") {
		const list = def as ListElement;
		const refProps = prepRefTableElement(presenter, list, container);

		if (presenter.readOnly) {
			const val = data.getValue(def.id);
			const text = refProps.getDisplayFor(val);
			component = <ReadOnlyValue>{text}</ReadOnlyValue>;
		} else {	
			component = 
				<List
					{...defaultProps} 
					items={refProps.refData ? refProps.refData.rows : []}
					getValue={refProps.getValue}
					getDisplay={refProps.getDisplay}
					multiSelect={list.multiSelect}
				/>;
		}

	} else if (def.type === "dropdown") {
		const dropdown = def as DropdownElement;
		const refProps = prepRefTableElement(presenter, dropdown, container);

		if (presenter.readOnly) {
			const val = data.getValue(def.id);
			const text = refProps.getDisplayFor(val);
			component = <ReadOnlyValue>{text}</ReadOnlyValue>;
		} else {	
			component = 
				<Dropdown
					{...defaultProps} 
					items={refProps.refData ? refProps.refData.rows : []}
					getValue={refProps.getValue}
					getDisplay={refProps.getDisplay}
					multiSelect={dropdown.multiSelect}
				/>;
		}	

	} else if (def.type === "dynamic-dropdown") {
		component = <DynamicDropdown def={def as DynamicDropdownElement} />


	} else if (def.type === "icon-choice") {
		const dropdown = def as IconChoiceElement;
		const refProps = prepRefTableElement(presenter, dropdown, container);

		if (presenter.readOnly) {
			const val = data.getValue(def.id);
			const text = refProps.getDisplayFor(val);
			component = <ReadOnlyValue>{text}</ReadOnlyValue>;
		} else {	
			component = 
				<IconChoice
					{...defaultProps} 
					items={refProps.refData ? refProps.refData.rows : []}
					def={def as IconChoiceElement}
					getValue={refProps.getValue}
					getDisplay={refProps.getDisplay}
				/>;
		}		

	} else if (def.type === "resource-picker") {
		component = 
			<ResourcePicker 
				{...defaultProps}
			/>

	} else if (def.type === "date") {
		if (presenter.readOnly) {
			const val = formatDate(data.getValue(def.id));
			component = <ReadOnlyValue>{val}</ReadOnlyValue>;

		} else {
			component = (
				<Date {...defaultProps} />
			);	
		}

		data.validators!.push(
			{
				validate: (context: ValidationContext<any>) => {
					if (!checkVisible()) return;

					validDateRule(context, def.id);
				}
			}
		);

	} else if (def.type === "legal-element") {
		component = (
			<LegalElement {...defaultProps} />
		);


	} else if (def.type === "container") {
		const container = def as ContainerElement;
		if (!container.elements) container.elements = [];
		component = (
			<Container def={container} key={def.id + designerKeyAppend} />
		);

	} else if (def.type === "sub-form") {
		// NOTE: want to treat it just like a container 
		// and prior to passing to Presenter the def should have been updated with prepareFormDef()
		component = (
			<Container def={def} key={def.id + designerKeyAppend} />
		);	

	} else if (def.type === "calc") {
		component = <Calc def={def as CalculatedElement} />

	} else if (def.type === "entity-lookup") {
		component = <EntityLookup def={def as EntityLookupElement} />

	} else if (def.type === "report") {
		component = <Report def={def as ReportElement} />

	} else if (def.type === "succession-plan") {
		component = <SuccessionPlan def={def as SuccessionPlanElement} />

	} else if (def.type === "group") {
		component = <Group def={def as GroupElement} />

	} else if (def.type === "checkbox-list") {
		let options = [];
		const ctx: EventContext = {
			container,
			dataWindows: [{
				name: "data",
				obj: container.data,
			}],
			def,
			presenter,
			type: "change",
			options: container.options,
			debugContext: [
				"CheckboxList.change"
			]
		};
		const cbl = def as CheckboxListElement;
		if (cbl.getOptionsScript) options = presenter.scriptEngine.run(cbl.getOptionsScript, ctx);
		component = <CheckboxList options={options} prop={def.id} />

	} else if (def.type === "chart") {
		component = <Chart def={def as ChartElement} />;

	} else if (def.type === "button") {
		const b = def as ButtonElement;
		component = 
			<button
				className="button primary"
				onClick={(e) => {
					if (b.onClick) {
						const ctx: EventContext = {
							container,
							dataWindows: [{
								name: "data",
								obj: container.data,
							}],				
							def,
							presenter,
							type: "change",
							options: container.options,
							debugContext: [
								"Button.onClick"
							]
						};

						presenter.scriptEngine.run(b.onClick, ctx);
					}
				}} 
			>{b.title}</button>;

	} else if (def.type === "gateway-form") {
		// return (
		// 	<GatewayForm
		// 		onClose={() => {
					
		// 		}}
		// 	/>
		// );
		
	} else if (def.type === "script") {
		const script = def as ScriptElement;

		if (presenter.readOnly) {
			component = <ReadOnlyValue>{data.getValue(def.id)}</ReadOnlyValue>;
		} else {
			component = 
				<JSEditor 
					{...defaultProps}
					lang={script.lang}
				/>
		}

 
	} else {
		alert("Unknown type: " + def.type);

	}

	data.validators!.push(
		{
			validate: (context: ValidationContext<any>) => {
				// only want to run validation if is visible
				if (!checkVisible()) return;

				actionValidator(context, def, presenter, container);
			}
		}
	);

	// want unique keys so if the def doesn't have an id then make one
	if (!def.id) def.id = newID();

	// default value
	if (def.defaultValue && defaultProps.prop && data.value[defaultProps.prop] === undefined) {
		data.value[defaultProps.prop] = def.defaultValue;
	}

	return (
		<Field
			def={def}
			component={component}
			key={def.id + designerKeyAppend} 

			noLabel={noLabel}
			noErrors={noErrors}
		/>
	);
}

